import React from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { Company } from '../../types/organization.types';
import { useCompanyTeams } from '../../hooks/useOrgQueries';
import { LedgerSkeleton } from '../shell/LedgerSkeleton';
import { cn } from '../../lib/cn';

// Same date rules as RegisterPeek/TaskPeek: local time for timestamps.
const stampLocal = (iso: string | null | undefined): string | null =>
  iso ? format(new Date(iso), 'MMM d, yyyy').toUpperCase() : null;

interface CompanyPeekProps {
  company: Company;
  onClose: () => void;
  onEdit: (id: string) => void;
  onDelete: (company: Company) => void;
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div className="cap-label">{label}</div>
    <div className="mt-0.5 text-scale-1 text-ink">{children}</div>
  </div>
);

export const CompanyPeek: React.FC<CompanyPeekProps> = ({ company, onClose, onEdit, onDelete }) => {
  const teams = useCompanyTeams(company.id);
  const updatedStamp = stampLocal(company.updatedAt);

  return (
    <aside aria-label="Company peek" className="border-l border-ink bg-paper-raised p-5">
      <div className="flex items-baseline justify-between">
        <span className="cap-label">COMPANY</span>
        <span className="flex gap-3">
          <button type="button" onClick={() => onEdit(company.id)} className="cap-label cursor-pointer !text-accent hover:underline">
            Edit ↗
          </button>
          <button type="button" onClick={() => onDelete(company)} className="cap-label cursor-pointer text-ink-muted hover:!text-sev-critical">
            Delete
          </button>
          <button type="button" aria-label="Close peek" onClick={onClose} className="cursor-pointer text-scale-0 text-ink-muted hover:text-ink">
            ✕
          </button>
        </span>
      </div>

      <h2 className="mt-1.5 font-serif text-scale-4 font-medium text-ink">{company.name}</h2>
      {company.description && <p className="mb-3 text-scale-1 text-ink-muted">{company.description}</p>}
      <div className="border-b border-ink" />

      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 py-3">
        <Field label="Email">
          {company.email ? (
            <a href={`mailto:${company.email}`} className="break-all font-mono text-accent hover:underline">
              {company.email}
            </a>
          ) : (
            '—'
          )}
        </Field>
        <Field label="Phone">
          {company.phone ? (
            <a href={`tel:${company.phone}`} className="break-all font-mono text-accent hover:underline">
              {company.phone}
            </a>
          ) : (
            '—'
          )}
        </Field>
        <Field label="Website">
          {company.website ? (
            <a
              href={company.website}
              target="_blank"
              rel="noreferrer"
              className="break-all font-mono text-accent hover:underline"
            >
              {company.website}
            </a>
          ) : (
            '—'
          )}
        </Field>
        <Field label="Users">
          <span className="font-mono">{company.usersCount}</span>
        </Field>
        {/* Display-only by design: company isActive is edited in the edit dialog, not inline. */}
        <Field label="Status">
          <span className="flex items-center gap-2">
            <span aria-hidden className={cn('h-2.5 w-2.5 border border-ink', company.isActive && 'bg-ink')} />
            <span className="text-scale-1 text-ink">{company.isActive ? 'Active' : 'Inactive'}</span>
          </span>
        </Field>
      </div>

      <div className="cap-label mt-4 border-b border-ink pb-0.5">Teams</div>
      {teams.isPending ? (
        <LedgerSkeleton lines={3} className="mt-2" />
      ) : teams.data && teams.data.length > 0 ? (
        <ul>
          {teams.data.map((team) => (
            <li key={team.id} className="flex items-baseline justify-between border-b border-rule-faint py-1.5 text-scale-1">
              <span className="text-ink">{team.name}</span>
              <span className="font-mono text-ink-muted">{team.usersCount}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="pt-1.5 font-serif text-scale-1 italic text-ink-muted">No teams yet.</p>
      )}
      <Link
        to={`/organizations/teams?company=${company.id}`}
        className="cap-label mt-1.5 inline-block cursor-pointer !text-accent hover:underline"
      >
        View teams ↗
      </Link>

      <div className="cap-label mt-4 border-b border-ink pb-0.5">History</div>
      <div className="pt-1.5 font-serif text-scale-1 italic text-ink-muted">
        <div>Recorded {stampLocal(company.createdAt)}</div>
        {updatedStamp && <div>Updated {updatedStamp}</div>}
      </div>
    </aside>
  );
};
